import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { IconButton, ThumbsDownIcon, ThumbsUpIcon } from '@utilities';

import FeedbackPopover from './components/FeedbackPopover';

import styles from './_index.module.scss';

const FeedbackFooter = ({
  disabled = false,
  feedbackId,
  feedbackLabel,
  initialFeedback,
  negativeFeedbackOptions,
  onSubmit,
  positiveFeedbackOptions,
}) => {
  const initialFeedbackState = {
    like: initialFeedback?.like ?? null,
    reason: initialFeedback?.reason || '',
    reasonExplanation: initialFeedback?.reasonExplanation || '',
  };
  const [feedbackState, setFeedbackState] = useState(initialFeedbackState);
  const [isHelpfulPopoverOpen, setHelpfulPopoverOpen] = useState(false);
  const [isNotHelpfulPopoverOpen, setNotHelpfulPopoverOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedFeedbackState, setSubmittedFeedbackState] = useState(initialFeedbackState);

  const helpfulButtonRef = useRef(null);
  const notHelpfulButtonRef = useRef(null);

  const handleSubmitFeedback = async () => {
    try {
      setIsSubmitting(true);
      const { like, reason, reasonExplanation } = feedbackState;

      await onSubmit({ feedbackId, like, reason, reasonExplanation });
      setSubmittedFeedbackState(feedbackState);
      setHelpfulPopoverOpen(false);
      setNotHelpfulPopoverOpen(false);
    } catch (error) {
      console.error('Feedback submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePopover = ({ isOpen, isPositive, setPopoverOpen }) => {
    setPopoverOpen((prev) => {
      const nextOpen = isOpen;
      const nextHelpfulOpen =
        setPopoverOpen === setHelpfulPopoverOpen ? nextOpen : isHelpfulPopoverOpen;
      const nextNotHelpfulOpen =
        setPopoverOpen === setNotHelpfulPopoverOpen ? nextOpen : isNotHelpfulPopoverOpen;

      if (isOpen) {
        if (
          !submittedFeedbackState.reason &&
          (!feedbackState.reason ||
            (feedbackState.like !== null && feedbackState.like !== isPositive))
        ) {
          const options = isPositive ? positiveFeedbackOptions : negativeFeedbackOptions;
          setFeedbackState((prev) => {
            const newState = {
              ...prev,
              like: isPositive,
              reason: options[0]?.value,
              reasonExplanation: '',
            };
            return newState;
          });
        }
      } else if (!isSubmitting && !nextHelpfulOpen && !nextNotHelpfulOpen) {
        const resetState = submittedFeedbackState.reason
          ? submittedFeedbackState
          : initialFeedbackState;
        setFeedbackState(resetState);
      }
      return nextOpen;
    });
  };

  return (
    <div className={styles['feedback-footer']}>
      <div
        className={classNames(styles['feedback-footer-text'], {
          [styles['text-disabled']]: disabled,
        })}
      >
        {feedbackLabel}
      </div>
      <div className={styles['feedback-footer-actions']}>
        <IconButton
          aria-label="This was helpful"
          className={isHelpfulPopoverOpen || feedbackState.like === true ? styles['active'] : ''}
          isDisabled={disabled}
          onClick={() =>
            togglePopover({ isOpen: true, isPositive: true, setPopoverOpen: setHelpfulPopoverOpen })
          }
          ref={helpfulButtonRef}
        >
          <ThumbsUpIcon />
        </IconButton>
        <IconButton.Danger
          aria-label="This was not helpful"
          className={
            isNotHelpfulPopoverOpen || feedbackState.like === false ? styles['active'] : ''
          }
          isDisabled={disabled}
          onClick={() =>
            togglePopover({
              isOpen: true,
              isPositive: false,
              setPopoverOpen: setNotHelpfulPopoverOpen,
            })
          }
          ref={notHelpfulButtonRef}
        >
          <ThumbsDownIcon />
        </IconButton.Danger>
      </div>

      <FeedbackPopover
        feedbackState={feedbackState}
        feedbackTitle="Why did you give this a thumbs up?"
        handleSubmitFeedback={handleSubmitFeedback}
        inputTestId="feedback-other-input-helpful"
        isActive={isHelpfulPopoverOpen}
        isSubmitting={isSubmitting}
        like={true}
        onHide={() =>
          togglePopover({ isOpen: false, isPositive: true, setPopoverOpen: setHelpfulPopoverOpen })
        }
        options={positiveFeedbackOptions}
        setFeedbackState={setFeedbackState}
        target={helpfulButtonRef?.current}
      />

      <FeedbackPopover
        feedbackState={feedbackState}
        feedbackTitle="Why did you give this a thumbs down?"
        handleSubmitFeedback={handleSubmitFeedback}
        inputTestId="feedback-other-input-not-helpful"
        isActive={isNotHelpfulPopoverOpen}
        isSubmitting={isSubmitting}
        like={false}
        onHide={() =>
          togglePopover({
            isOpen: false,
            isPositive: false,
            setPopoverOpen: setNotHelpfulPopoverOpen,
          })
        }
        options={negativeFeedbackOptions}
        setFeedbackState={setFeedbackState}
        target={notHelpfulButtonRef?.current}
      />
    </div>
  );
};

FeedbackFooter.propTypes = {
  disabled: PropTypes.bool,
  feedbackId: PropTypes.number.isRequired,
  feedbackLabel: PropTypes.node,
  initialFeedback: PropTypes.shape({
    like: PropTypes.bool,
    reason: PropTypes.string,
    reasonExplanation: PropTypes.string,
  }),
  negativeFeedbackOptions: PropTypes.array,
  onSubmit: PropTypes.func,
  positiveFeedbackOptions: PropTypes.array,
};

export default FeedbackFooter;
